import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import {
  FaWhatsapp,
  FaRegFilePdf,
  FaMapMarkerAlt,
  FaTimes,
  FaFilter,
  FaSyncAlt,
} from "react-icons/fa";
import { BsEye, BsEyeSlash } from "react-icons/bs";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { Tabs } from "../../Component/Common/Tabs";
import Dropdown from "../../Component/Common/Dropdown";
import { InputField } from "../../Component/Common/InputField";
import SwitchField from "../../Component/Common/SwitchField";
// import CustomPagination from "../../Component/Common/CustomPagination"; // pagination removed
import ConfirmModal from "../../Component/Common/ConstomModal/ConfirmModal";
import InputModal from "../../Component/Common/ConstomModal/InputModal";
import { ROLE, FEATURES, hasAccess, getAccessLevel, ACCESS, handleKeyDown, fullVoterTabList } from "../../helper";
import { VoterTable } from "./components/VoterTable";
import { useVoterListFetch } from "./components/useVoterListFetch";
import {
  // DEFAULT_PAGE_SIZE, PER_PAGE_OPTIONS - only used by the removed pagination bar
  colorLIst,
  colourLabel,
  votedLIst,
  voterTypeList,
} from "./components/voterHelpers";
import NotoDevanagari from "../FullVoter/notoDevanagari";

// Shared view backing both Voter Search (search-first: filters always
// visible, results hidden until Search is clicked) and Voter Management's
// Dashboard (shows the full list immediately on load, matching the old
// monolithic FullVoter.js's default behavior - no search gate). Filters +
// results table + booth address banner + stats bar + pagination footer,
// plus the full toolbar (WhatsApp share, PDF export, Bulk Update, links to
// the Delete/Upload/Add Voter pages). Add/Upload/Delete-By-Ac-No are no
// longer local modals here - they're their own routed pages (see
// DeleteVoter.js/UploadVoter.js/AddVoter.js), so clicking those toolbar
// buttons now navigates instead of opening an overlay.
const VoterListView = ({ searchFirst = true }) => {
  const navigate = useNavigate();
  const v = useVoterListFetch();

  // Search-first: filters are always visible, but the results table/booth
  // banner/stats/pagination stay hidden until the user submits a search
  // (the data layer still fetches in the background via the existing
  // debounced filter effect, so results are ready the instant they're
  // revealed - only the reveal itself is gated, not the underlying fetch).
  // Not search-first (Dashboard): results are visible from the start.
  const [hasSearched, setHasSearched] = useState(!searchFirst);

  const [showModal, setShowModal] = useState(false);
  const [mobileNumber, setMobileNumber] = useState("");
  const [inputError, setInputError] = useState({});

  const [bulkUpdateModal, setBulkUpdateModal] = useState(false);
  const [bulkForm, setBulkForm] = useState({});
  const [bulkFields, setBulkFields] = useState({});
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkUpdateLoading, setBulkUpdateLoading] = useState(false);

  // Pass this page's path so the voter form closes back here (e.g. the
  // Dashboard), not always to Voter Search.
  const location = useLocation();
  const backState = { state: { from: location.pathname } };
  const editVoter = (item) => navigate(`/voter-management/add?editId=${item.id}`, backState);
  const viewVoter = (item) => navigate(`/voter-management/add?editId=${item.id}&view=1`, backState);

  const handleWhatsappSubmit = () => {
    if (!mobileNumber) {
      setInputError({ mobileNumber: "Please enter a valid mobile number" });
      return;
    }
    const message = v.selectedItems
      .map(
        (voter) => `
Vote For BJP
Booth No : ${voter.booth_no}     Sr No : ${voter.sr_no}
Name : ${voter.full_name_h}
Relative Name : ${voter.last_name_h}
Gender/Age : ${voter.gender_e}/${voter.age}
Address : ${voter.house_no} - ${voter.address_h || "N/A"}
Polling Booth : ${voter.booth_address_h || "N/A"}

--------------------------------------------`
      )
      .join("\n");

    const footerMessage = `यह आपकी वोटर स्लिप है इसे दिखा कर 20 नवंबर बुधवार को भारतीय जनता पार्टी के टेबल से आप अपनी वोटर स्लिप लेकर आप अपना कीमती वोट डाल सकते है
हर वोट जरूरी है कृपया वोट जरूर दे

आपका सुरेश अवस्थी (प्रत्याशी 77-लोकसभा वाराणसी)
भा जा पा को वोट दे`;

    const fullMessage = `${message}\n\n${footerMessage}`;
    const encodedMessage = encodeURIComponent(fullMessage);
    window.open(`https://wa.me/${mobileNumber}?text=${encodedMessage}`, "_blank");
    setShowModal(false);
    setMobileNumber("");
  };

  const handleGeneratePDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.addFileToVFS("NotoSansDevanagari.ttf", NotoDevanagari);
    doc.addFont("NotoSansDevanagari.ttf", "NotoDevanagari", "normal");

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const left = 10;
    const right = 10;
    const tableWidth = pageWidth - left - right;

    const currentYear = new Date().getFullYear();
    const assemblyLabel = v.formOrderName?.assemblyFilter?.label || v.filter?.assembly_number || "-";
    const boothLabel = v.formOrderName?.boothFilter?.label || v.filter?.booth_number || "-";
    const partyName = v.userLogin?.party_name || "-";
    const totalVoters = v.selectedItems.length;

    const maleCount = v.selectedItems.filter((item) => /^m/i.test(item.gender_e || item.gender_h || "")).length;
    const femaleCount = v.selectedItems.filter((item) => /^f|^w/i.test(item.gender_e || item.gender_h || "")).length;

    const colourInitial = (val) => {
      if (!val || val === "-1" || val === -1 || val === "") return "-";
      const label = colourLabel(val);
      if (!label || label === "-") return "-";
      return label.charAt(0).toUpperCase();
    };

    const votedDisplay = (val) => (val === true || val === 1 || val === "true" || val === "1" ? "Y" : "-");

    const generatedDate = new Date().toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    const colWidths = {
      v_no: 16,
      house_no: 16,
      epic_no: 36,
      mobile_no: 30,
      colour: 18,
      voted: 16,
      cast_no: v.showCastNo ? 18 : 0,
    };
    const fixedTotal = Object.values(colWidths).reduce((a, b) => a + b, 0);
    const nameRelTotal = tableWidth - fixedTotal;
    colWidths.name = Math.round(nameRelTotal * 0.52);
    colWidths.rel_name = nameRelTotal - colWidths.name;

    const drawPageHeader = (docRef) => {
      let y = 10;
      const totalW = tableWidth;
      const rightColW = 52;
      const mainW = totalW - rightColW;

      docRef.setDrawColor(0, 0, 0);
      docRef.setLineWidth(0.4);

      docRef.rect(left, y, mainW, 12);
      docRef.setFont("helvetica", "bold");
      docRef.setFontSize(14);
      docRef.text(`ELECTORAL ROLL ${currentYear}`, left + mainW / 2, y + 8.5, { align: "center" });

      docRef.rect(left + mainW, y, rightColW, 12);
      docRef.setFont("helvetica", "normal");
      docRef.setFontSize(9);
      docRef.text(`Total Voter = ${totalVoters}`, left + mainW + 3, y + 7.5);
      y += 12;

      docRef.rect(left, y, mainW, 9);
      docRef.setFont("helvetica", "bold");
      docRef.setFontSize(9.5);
      docRef.text(`Assembly Constituency : ${assemblyLabel}`, left + 3, y + 6);

      docRef.rect(left + mainW, y, rightColW, 9);
      docRef.setFont("helvetica", "normal");
      docRef.setFontSize(9);
      docRef.text(`M = ${maleCount} , F = ${femaleCount}`, left + mainW + 3, y + 6);
      y += 9;

      const col1W = mainW * 0.28;
      const col2W = mainW * 0.32;
      const col3W = mainW - col1W - col2W + rightColW;

      docRef.rect(left, y, col1W, 9);
      docRef.setFont("helvetica", "bold");
      docRef.setFontSize(9);
      docRef.text(`Booth : ${boothLabel}`, left + 3, y + 6);

      docRef.rect(left + col1W, y, col2W, 9);
      docRef.setFont("helvetica", "normal");
      docRef.text(`Party : ${partyName}`, left + col1W + 3, y + 6);

      docRef.rect(left + col1W + col2W, y, col3W, 9);
      docRef.text(`Generated : ${generatedDate}`, left + col1W + col2W + 3, y + 6);
      y += 9;

      const uniqueSections = [
        ...new Set(v.selectedItems.map((item) => item.address_h || item.address_e || "").filter(Boolean)),
      ]
        .slice(0, 3)
        .join(" , ");

      docRef.setFont("NotoDevanagari", "normal");
      docRef.setFontSize(8.5);
      const sectionLines = docRef.splitTextToSize(`Sections : ${uniqueSections || "-"}`, totalW - 6);
      const sectionRowH = Math.max(9, sectionLines.length * 5.5 + 4);
      docRef.rect(left, y, totalW, sectionRowH);
      docRef.text(sectionLines, left + 3, y + 5.5);
      y += sectionRowH;

      const pollingStation = v.selectedItems[0]?.booth_address_h || v.selectedItems[0]?.booth_address_e || "-";
      docRef.setFont("NotoDevanagari", "normal");
      docRef.setFontSize(8.5);
      const pollingLines = docRef.splitTextToSize(`Polling Station : ${pollingStation}`, totalW - 6);
      const pollingRowH = Math.max(9, pollingLines.length * 5.5 + 4);
      docRef.rect(left, y, totalW, pollingRowH);
      docRef.text(pollingLines, left + 3, y + 5.5);
      y += pollingRowH;

      docRef.setFont("helvetica", "normal");
      return y;
    };

    const startY = drawPageHeader(doc);

    const columns = [
      { header: "V.No.", dataKey: "v_no" },
      { header: "H.No", dataKey: "house_no" },
      { header: "Name", dataKey: "name" },
      { header: "Rel. Name", dataKey: "rel_name" },
      { header: "EPIC No.", dataKey: "epic_no" },
      { header: "Mobile", dataKey: "mobile_no" },
      { header: "Colour", dataKey: "colour" },
      { header: "Voted", dataKey: "voted" },
      ...(v.showCastNo ? [{ header: "Category", dataKey: "cast_no" }] : []),
    ];

    const body = v.selectedItems.map((item, index) => {
      const firstName = item.first_name_e || item.first_name_h || "";
      const middleName = item.middle_name_e || item.middle_name_h || "";
      const lastName = item.last_name_e || item.last_name_h || "";
      const fullName =
        item.full_name_e || item.full_name_h || [firstName, middleName, lastName].filter(Boolean).join(" ") || "-";

      return {
        v_no: item.voter_sr_number || item.sr_no || index + 1,
        house_no: item.house_no || "-",
        name: fullName,
        rel_name: item.last_name_e || item.last_name_h || "-",
        epic_no: item.epic_no || "-",
        mobile_no: item.mobile_no || "-",
        colour: colourInitial(item.colour),
        voted: votedDisplay(item.is_voted),
        ...(v.showCastNo ? { cast_no: item?.category ?? item?.cast_no ?? item?.sr_no ?? "-" } : {}),
      };
    });

    doc.autoTable({
      columns,
      body,
      startY,
      theme: "grid",
      tableWidth,
      styles: {
        font: "helvetica",
        fontSize: 8.5,
        cellPadding: { top: 3, bottom: 3, left: 2.5, right: 2.5 },
        valign: "middle",
        lineColor: [0, 0, 0],
        lineWidth: 0.25,
        textColor: [20, 20, 20],
        overflow: "linebreak",
      },
      headStyles: {
        font: "helvetica",
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        fontSize: 9,
        halign: "center",
        lineColor: [0, 0, 0],
        lineWidth: 0.4,
      },
      bodyStyles: { fillColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: [255, 255, 255] },
      columnStyles: {
        v_no: { halign: "center", cellWidth: colWidths.v_no },
        house_no: { halign: "center", cellWidth: colWidths.house_no },
        name: { cellWidth: colWidths.name },
        rel_name: { cellWidth: colWidths.rel_name },
        epic_no: { halign: "center", cellWidth: colWidths.epic_no },
        mobile_no: { halign: "center", cellWidth: colWidths.mobile_no },
        colour: { halign: "center", cellWidth: colWidths.colour },
        voted: { halign: "center", cellWidth: colWidths.voted },
        ...(v.showCastNo ? { cast_no: { halign: "center", cellWidth: colWidths.cast_no } } : {}),
      },
      rowPageBreak: "avoid",
      didParseCell: (data) => {
        if (data.column.dataKey === "voted" && data.section === "body" && data.cell.raw === "Y") {
          data.cell.styles.textColor = [20, 140, 70];
          data.cell.styles.fontStyle = "bold";
        }
        if (data.column.dataKey === "v_no" && data.section === "body") {
          data.cell.styles.fontStyle = "bold";
        }
        if (["name", "rel_name"].includes(data.column.dataKey) && data.section === "body") {
          data.cell.styles.font = "NotoDevanagari";
          data.cell.styles.fontSize = 8;
        }
      },
      didDrawPage: (data) => {
        if (data.pageNumber > 1) drawPageHeader(doc);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(120, 120, 120);
        doc.text("Generated by ElectionEasy", left, pageHeight - 6);
        doc.text(`Page ${data.pageNumber} of ${doc.internal.getNumberOfPages()}`, pageWidth - right, pageHeight - 6, {
          align: "right",
        });
        doc.setTextColor(0, 0, 0);
      },
      margin: { top: startY, bottom: 14, left, right },
    });

    doc.save(`ElectoralRoll_Booth${boothLabel}_${currentYear}.pdf`);
  };

  // --- Bulk Update -----------------------------------------------------------
  const BULK_EDITABLE_FIELDS = [
    { name: "colour", label: "Colour", type: "select", options: colorLIst },
    { name: "profession", label: "Profession", type: "select", options: v.profList },
    { name: "post", label: "Post", type: "select", options: v.postList },
    { name: "caste", label: "Caste", type: "select", options: v.castList },
    { name: "is_voted", label: "Is Voted", type: "checkbox" },
    { name: "address_change", label: "Address Change", type: "text" },
  ];

  const openBulkUpdateModal = () => {
    if (!v.selectedItems?.length) {
      toast.error("Please select at least one voter");
      return;
    }
    setBulkForm({});
    setBulkFields({});
    setBulkUpdateModal(true);
  };

  const isAllBulkFieldsChecked =
    BULK_EDITABLE_FIELDS.length > 0 && BULK_EDITABLE_FIELDS.every((f) => bulkFields[f.name]);

  const handleBulkSelectAll = () => {
    setBulkFields((current) => {
      const allChecked = BULK_EDITABLE_FIELDS.every((f) => current[f.name]);
      const next = {};
      BULK_EDITABLE_FIELDS.forEach((f) => {
        next[f.name] = !allChecked;
      });
      return next;
    });
  };

  const handleBulkClearAll = () => {
    setBulkFields({});
    setBulkForm({});
  };

  const handleBulkSubmitClick = () => {
    if (!v.selectedItems?.length) {
      toast.error("Please select at least one voter");
      return;
    }
    const checkedFields = Object.keys(bulkFields).filter((key) => bulkFields[key]);
    if (!checkedFields.length) {
      toast.error("Please select at least one field to update");
      return;
    }
    setBulkConfirmOpen(true);
  };

  const handleBulkConfirmSubmit = () => {
    setBulkUpdateLoading(true);
    v.bulkUpdateSubmit(bulkFields, bulkForm, {
      onSuccess: () => {
        setBulkUpdateModal(false);
        setBulkConfirmOpen(false);
        setBulkForm({});
        setBulkFields({});
      },
      onSettled: () => setBulkUpdateLoading(false),
    });
  };

  return (
    <div className="voter-page-shell">
      <div className="tabMain_head voter-page-header">
        {" "}
        <div className="Tab_dropList">
          <Tabs TabList={fullVoterTabList} handleActive={() => {}} activeTab={v.filter.status} />
          <div className="flex_assambly_drop">
            <div>
              <Dropdown
                className="dropdownoforder"
                placeholder="Select Assembly Number"
                name="assembly_number"
                options={v.assamblyList || []}
                valueText="value"
                labelText="label"
                isClearable
                selectedOption={
                  v.formOrderName?.assemblyFilter ||
                  (v.userLogin?.user_type !== ROLE.SUPER_ADMIN && v.assamblyList[0])
                }
                setSelectedOption={v.handleAssemblyChange}
              />
            </div>
            <div>
              <Dropdown
                className="dropdownoforder"
                placeholder="Select Booth Number"
                name="booth_number"
                options={v.boothList || []}
                valueText="value"
                labelText="label"
                isClearable
                selectedOption={v.formOrderName?.boothFilter}
                setSelectedOption={v.handleBoothChange}
              />
            </div>
          </div>
        </div>
        <div className="checkBox_Cnacle voter-header-actions">
          {v.selectedItems.length > 0 && (
            <span className="voter-selection-count">Total Records : {v.selectedItems.length} selected</span>
          )}

          {v.selectedItems.length > 0 && hasAccess(v.userLogin?.user_type, FEATURES.BULK_UPDATE) && (
            <button className="voter-btn-danger-outline" onClick={() => v.setSelectedItems([])}>
              Cancel
            </button>
          )}

          <button
            type="button"
            className="voter-action-icon whatsapp"
            title="Share on WhatsApp"
            onClick={() => (v.selectedItems?.length ? setShowModal(true) : toast.error("Please Select Voter"))}
          >
            <FaWhatsapp />
          </button>

          <button
            type="button"
            className="voter-action-icon export"
            title="Export"
            onClick={() => (v.selectedItems?.length ? handleGeneratePDF() : toast.error("Please Select Voter"))}
          >
            <FaRegFilePdf />
          </button>

          {/* Bulk upload shortcut on the Dashboard only (Voter Search keeps its
              toolbar minimal); the upload page returns here via state.from. */}
          {!searchFirst && getAccessLevel(v.userLogin?.user_type, FEATURES.UPLOAD_EXCEL) === ACCESS.FULL && (
            <button
              className="addPlan"
              onClick={() => navigate("/voter-management/upload", { state: { from: "/voter-management/dashboard" } })}
            >
              Upload Voters
            </button>
          )}

          {/* {getAccessLevel(v.userLogin?.user_type, FEATURES.DELETE_VOTER) === ACCESS.FULL && (
            <button className="voter-btn-danger-outline" onClick={() => navigate("/voter-management/delete")}>
              Delete By Ac No
            </button>
          )}

          {getAccessLevel(v.userLogin?.user_type, FEATURES.UPLOAD_EXCEL) === ACCESS.FULL && (
            <button className="addPlan" onClick={() => navigate("/voter-management/upload")}>
              Upload Voters
            </button>
          )}

          {hasAccess(v.userLogin?.user_type, FEATURES.ADD_VOTER) && (
            <button className="addPlan" onClick={() => navigate("/voter-management/add", backState)}>
              Add Voter
            </button>
          )}

          {hasAccess(v.userLogin?.user_type, FEATURES.BULK_UPDATE) && (
            <button className="addPlan" onClick={openBulkUpdateModal}>
              Bulk Update
            </button>
          )} */}
        </div>
      </div>

      {hasSearched && v.filter?.booth_number && (
        <div className="voter-booth-banner">
          <div className="voter-booth-banner-info">
            <FaMapMarkerAlt className="voter-booth-banner-icon" />
            <span className="voter-booth-banner-label">Booth Address :</span>
            <span className="voter-booth-banner-address">{v.boothAddress || "-"}</span>
          </div>
          <button type="button" className="voter-clear-booth-btn" onClick={() => v.handleBoothChange(null)}>
            <FaTimes />
            <span>Clear Booth Address</span>
          </button>
        </div>
      )}

      <div className="anvTask_prog voter-filter-section">
        <div className="voter-filter-heading">
          <FaFilter />
          <span>Filters</span>
        </div>
        <div className="voter-filter-bar voter-filter-bar--fixed8">
          <div className="voter-filter-item">
            <InputField
              label="Epic No."
              labelClass="voter-filter-label"
              placeholder="Epic No."
              className="anvSearch_input voter-filter-input"
              boxClass="voter-filter-control"
              value={v.filter?.epic_number || ""}
              onChange={(e) => v.updateListFilter({ epic_number: e.target.value })}
            />
          </div>
          <div className="voter-filter-item">
            <InputField
              label="First Name"
              labelClass="voter-filter-label"
              placeholder="First Name"
              className="anvSearch_input voter-filter-input"
              boxClass="voter-filter-control"
              value={v.filter?.first_name_e || ""}
              onChange={(e) => v.updateListFilter({ first_name_e: e.target.value })}
            />
          </div>
          <div className="voter-filter-item">
            <InputField
              label="Last Name"
              labelClass="voter-filter-label"
              placeholder="Last Name"
              className="anvSearch_input voter-filter-input"
              boxClass="voter-filter-control"
              value={v.filter?.last_name_e || ""}
              onChange={(e) => v.updateListFilter({ last_name_e: e.target.value })}
            />
          </div>
          <div className="voter-filter-item">
            <InputField
              label="Mobile Number"
              labelClass="voter-filter-label"
              placeholder="Mobile Number"
              className="anvSearch_input voter-filter-input"
              boxClass="voter-filter-control"
              maxLength={15}
              value={v.filter?.mobile_no || ""}
              onKeyDown={handleKeyDown}
              onChange={(e) => v.updateListFilter({ mobile_no: e.target.value })}
            />
          </div>
          <div className="voter-filter-item">
            <label className="voter-filter-label">Select Voter Type</label>
            <Dropdown
              placeholder="All Voter Types"
              className="voter-filter-select"
              classNamePrefix="voterFilterSelect"
              boxClass="voter-filter-control"
              name="voter_type"
              options={voterTypeList}
              valueText="value"
              labelText="label"
              isClearable
              selectedOption={v.formOrderName?.voterTypeFilter}
              setSelectedOption={(data) => {
                v.updateListFilter({ voter_type: data?.value });
                v.setFormOrderName((current) => ({ ...current, voterTypeFilter: data }));
              }}
            />
          </div>
          <div className="voter-filter-item">
            <label className="voter-filter-label">Select Colour</label>
            <Dropdown
              placeholder="All Colours"
              className="voter-filter-select"
              classNamePrefix="voterFilterSelect"
              boxClass="voter-filter-control"
              name="colour"
              options={colorLIst || []}
              valueText="value"
              labelText="label"
              isClearable
              selectedOption={v.formOrderName?.colourFilter}
              setSelectedOption={(data) => {
                v.updateListFilter({ colour: data?.value });
                v.setFormOrderName((current) => ({ ...current, colourFilter: data }));
              }}
            />
          </div>
          <div className="voter-filter-item">
            <label className="voter-filter-label">Select Voted</label>
            <Dropdown
              placeholder="All"
              className="voter-filter-select"
              classNamePrefix="voterFilterSelect"
              boxClass="voter-filter-control"
              name="voted"
              options={votedLIst || []}
              valueText="value"
              labelText="label"
              isClearable
              selectedOption={v.formOrderName?.votedFilter}
              setSelectedOption={(data) => {
                v.updateListFilter({ voted: data?.value, is_voted: data?.value });
                v.setFormOrderName((current) => ({ ...current, votedFilter: data }));
              }}
            />
          </div>
          <div className="voter-filter-item">
            <label className="voter-filter-label">Select Caste</label>
            <Dropdown
              placeholder="All Castes"
              className="voter-filter-select"
              classNamePrefix="voterFilterSelect"
              boxClass="voter-filter-control"
              name="cast"
              options={v.castList || []}
              valueText="value"
              labelText="label"
              isClearable
              selectedOption={v.formOrderName?.castFilter}
              setSelectedOption={(data) => {
                v.updateListFilter({ cast: data?.value });
                v.setFormOrderName((current) => ({ ...current, castFilter: data }));
              }}
            />
          </div>
        </div>

        <div className="voter-filter-actions">
          <button
            type="button"
            className={`voter-filter-outline-btn ${v.showCastNo ? "is-active" : ""}`}
            onClick={() => v.setShowCastNo((prev) => !prev)}
            aria-pressed={v.showCastNo}
            title={v.showCastNo ? "Hide Category" : "Show Category"}
          >
            {v.showCastNo ? <BsEyeSlash /> : <BsEye />}
            <span>{v.showCastNo ? "Hide Category" : "Show Category"}</span>
          </button>
          <button
            type="button"
            className={`voter-filter-outline-btn ${v.showMobileNumbers ? "is-active" : ""}`}
            onClick={() => v.setShowMobileNumbers((current) => !current)}
            aria-pressed={v.showMobileNumbers}
            title={v.showMobileNumbers ? "Hide mobile numbers" : "Show mobile numbers"}
          >
            {v.showMobileNumbers ? <BsEyeSlash /> : <BsEye />}
            <span>{v.showMobileNumbers ? "Hide Mobile" : "Show Mobile"}</span>
          </button>
          <div
            className="voter-has-mobile-toggle"
            title={
              v.loaderForList
                ? "Loading voters…"
                : v.loadError
                ? "Voter list failed to load - refresh before filtering"
                : undefined
            }
          >
            <SwitchField
              label="Has Mobile Number"
              value={v.hasMobileOnly}
              disabled={v.loaderForList || !!v.loadError}
              onSwitchChange={() => {
                v.setHasMobileOnly((prev) => !prev);
                v.setMobileOnlyPage(0);
              }}
            />
          </div>

          <div className="voter-filter-actions-spacer" />

          {searchFirst && (
            <button type="button" className="addPlan voter-filter-search-btn" onClick={() => setHasSearched(true)}>
              Search
            </button>
          )}
          <button
            type="button"
            className="voter-filter-clear-btn"
            onClick={() => {
              v.handleClearAdvancedFilters();
              if (searchFirst) setHasSearched(false);
            }}
          >
            <FaSyncAlt />
            <span>Clear Filters</span>
          </button>
        </div>
      </div>

      {!hasSearched ? (
        <div className="voter-stats-empty" style={{ margin: 16 }}>
          Set your filters and click Search to see results.
        </div>
      ) : (
        <>
          <VoterTable
            voters={v.pageVoters}
            tableLoading={v.loaderForList}
            loadProgress={v.loadProgress}
            userLogin={v.userLogin}
            selectedIdSet={v.selectedIdSet}
            selectedOnPageCount={v.selectedOnPageCount}
            allSelected={v.allPageVotersSelected}
            onToggleSelect={v.handleCheckboxChange}
            onToggleSelectAll={v.handleSelectAll}
            showCastNo={v.showCastNo}
            showMobileNumbers={v.showMobileNumbers}
            onView={viewVoter}
            onEdit={editVoter}
            onDelete={(item) => v.hendleDeleteTag(item, true)}
          />

          <div className="voter-stats-bar">
        {v.isStatsLoading ? (
          <>
            {["Total Voters", "Voted", "Not Voted", "Supporter", "Neutral", "Opposition", "Not Assigned"].map(
              (label) => (
                <div className="voter-stat-card voter-stat-card-skeleton" key={label}>
                  <span className="voter-stat-label">{label}</span>
                  <span className="voter-stat-skeleton-bar" />
                </div>
              )
            )}
          </>
        ) : v.statsError ? (
          <div className="voter-stats-error">
            <span>{v.statsError}</span>
            <button type="button" className="voter-stats-retry-btn" onClick={v.retryStats}>
              <FaSyncAlt />
              <span>Retry</span>
            </button>
          </div>
        ) : (
          <>
            <div className="voter-stat-card">
              <span className="voter-stat-label">Total Voters</span>
              <span className="voter-stat-value total">{v.voterStats.total}</span>
            </div>
            <div className="voter-stat-card">
              <span className="voter-stat-label">Voted</span>
              <span className="voter-stat-value voted">{v.voterStats.voted}</span>
            </div>
            <div className="voter-stat-card">
              <span className="voter-stat-label">Not Voted</span>
              <span className="voter-stat-value not-voted">{v.voterStats.notVoted}</span>
            </div>
            <div className="voter-stat-card">
              <span className="voter-stat-label">Supporter</span>
              <span className="voter-stat-value supporter">{v.voterStats.supporter}</span>
            </div>
            <div className="voter-stat-card">
              <span className="voter-stat-label">Neutral</span>
              <span className="voter-stat-value neutral">{v.voterStats.neutral}</span>
            </div>
            <div className="voter-stat-card">
              <span className="voter-stat-label">Opposition</span>
              <span className="voter-stat-value opposition">{v.voterStats.opposition}</span>
            </div>
            <div className="voter-stat-card">
              <span className="voter-stat-label">Not Assigned</span>
              <span className="voter-stat-value not-assigned">{v.voterStats.notAssigned}</span>
            </div>
          </>
        )}
      </div>

      {v.loadedVoterTotal > 0 && (
        <div className="voter-pagination-bar">
          <div className="voter-pagination-info">
            Showing {v.activeVoterTotal.toLocaleString()} of {v.loadedVoterTotal.toLocaleString()} voters
            {/* Booth lists are always fetched fresh, so no time/Refresh. */}
            {!v.isBoothScope && v.lastLoadedAt && (
              <> · loaded at {new Date(v.lastLoadedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</>
            )}
          </div>
          {!v.isBoothScope && (
            <button
              type="button"
              className="voter-filter-outline-btn"
              onClick={v.refreshVoterList}
              disabled={v.loaderForList}
              title="Download the latest voter list from the server"
            >
              <FaSyncAlt />
              <span>Refresh</span>
            </button>
          )}
        </div>
      )}

      {/* Pagination removed - the whole list now loads in one request (see
          useVoterListFetch.getFullVoterList). Old bar kept for reference:
      {v.activeVoterTotal > 0 && (
        <div className="voter-pagination-bar">
          <div className="voter-pagination-info">
            Showing {v.paginationStart} to {v.paginationEnd} of {v.activeVoterTotal} entries
          </div>
          <div className="voter-pagination-controls">
            <div className="voter-per-page">
              <span>Per Page</span>
              <Dropdown
                className="voter-per-page-select"
                classNamePrefix="voterFilterSelect"
                options={PER_PAGE_OPTIONS}
                valueText="value"
                labelText="label"
                selectedOption={PER_PAGE_OPTIONS.find((option) => option.value === v.filter.limit)}
                setSelectedOption={(data) => {
                  v.setMobileOnlyPage(0);
                  v.setFilter((s) => ({
                    ...s,
                    limit: data?.value || DEFAULT_PAGE_SIZE,
                    offset: 0,
                    currentPage: 0,
                  }));
                }}
              />
            </div>
            {v.showPagination && (
              <div className="voter-pagination-nav">
                <button
                  type="button"
                  className="voter-page-edge-btn"
                  onClick={() => v.handlePageClick({ selected: 0 })}
                  disabled={!v.currentPageIndex}
                  aria-label="First page"
                >
                  ⏮
                </button>
                <CustomPagination
                  onPageChange={v.handlePageClick}
                  currentPage={v.currentPageIndex}
                  pageCount={v.pageCount}
                />
                <button
                  type="button"
                  className="voter-page-edge-btn"
                  onClick={() => v.handlePageClick({ selected: Math.max(v.pageCount - 1, 0) })}
                  disabled={v.currentPageIndex >= v.pageCount - 1}
                  aria-label="Last page"
                >
                  ⏭
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      */}
        </>
      )}

      <ConfirmModal isOpen={v.openDel} toggle={() => v.setOpenDel(!v.openDel)} onSubmit={v.handleDelete} />

      <InputModal
        isOpen={showModal}
        headerName="Whatsapp Massage"
        toggle={() => setShowModal(!showModal)}
        submitLabel="Send"
        onSubmit={handleWhatsappSubmit}
        disabled={mobileNumber.length !== 12}
        inputProps={
          <InputField
            className="modal_input"
            label="Mobile Number"
            labelClass="modal_label"
            labelSpan="*"
            labelSpanClass="style_for_star"
            placeholder="Enter 10 digit Mobile Number"
            name="mobileNumber"
            maxLength={10}
            onKeyDown={handleKeyDown}
            error={inputError?.mobileNumber}
            value={mobileNumber.startsWith("91") ? mobileNumber.slice(2) : mobileNumber}
            onChange={({ target: { value } }) => {
              const digits = value.replace(/\D/g, "").slice(0, 10);
              setMobileNumber(digits.length === 10 ? "91" + digits : digits);
            }}
          />
        }
      />

      <div
        className="modal"
        style={{ display: bulkUpdateModal ? "block" : "none" }}
      >
        {bulkUpdateModal && (
          <BulkUpdateModal
            selectedCount={v.selectedItems.length}
            fields={BULK_EDITABLE_FIELDS}
            bulkFields={bulkFields}
            bulkForm={bulkForm}
            isAllChecked={isAllBulkFieldsChecked}
            onSelectAll={handleBulkSelectAll}
            onClearAll={handleBulkClearAll}
            onFieldToggle={(name) => setBulkFields((c) => ({ ...c, [name]: !c[name] }))}
            onFieldChange={(name, val) => setBulkForm((c) => ({ ...c, [name]: val }))}
            onClose={() => setBulkUpdateModal(false)}
            onSubmit={handleBulkSubmitClick}
          />
        )}
      </div>

      <BulkConfirmModal
        isOpen={bulkConfirmOpen}
        count={v.selectedItems.length}
        loading={bulkUpdateLoading}
        onCancel={() => setBulkConfirmOpen(false)}
        onConfirm={handleBulkConfirmSubmit}
      />
    </div>
  );
};

// Search-first, per the restructure spec: filters visible, results hidden
// until the user clicks Search.
export const VoterSearch = () => <VoterListView searchFirst />;

// Not search-first: shows the full list immediately, matching the old
// monolithic FullVoter.js's default behavior.
export const VoterDashboardList = () => <VoterListView searchFirst={false} />;

// --- Small local sub-components for the Bulk Update modal pair -------------
// (kept in this file since Bulk Update is only reachable from Voter Search's
// toolbar, unlike VoterTable which is genuinely shared across 3 pages.)

const BulkUpdateModal = ({
  selectedCount,
  fields,
  bulkFields,
  bulkForm,
  isAllChecked,
  onSelectAll,
  onClearAll,
  onFieldToggle,
  onFieldChange,
  onClose,
  onSubmit,
}) => (
  <Modal size="lg" isOpen centered scrollable toggle={onClose}>
    <ModalHeader toggle={onClose}>Bulk Update Voters</ModalHeader>
    <ModalBody>
      <div style={{ marginBottom: 12, fontWeight: 600 }}>Selected Voters: {selectedCount}</div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 14,
          paddingBottom: 10,
          borderBottom: "1px solid #e5e5e5",
        }}
      >
        <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
          <input type="checkbox" checked={isAllChecked} onChange={onSelectAll} />
          <span>Select All Fields</span>
        </label>
        <Button color="secondary" size="sm" onClick={onClearAll}>
          Clear All Fields
        </Button>
      </div>
      <div className="anvOt_gridBx">
        {fields.map((field) => (
          <div className="anvOt_inputField" key={field.name}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={!!bulkFields[field.name]}
                onChange={() => onFieldToggle(field.name)}
              />
              <Label className="FormLableKaran" style={{ margin: 0 }}>
                {field.label}
              </Label>
            </label>
            {field.type === "select" ? (
              <Dropdown
                className="dropdownoforder"
                placeholder={`Select ${field.label}`}
                name={field.name}
                options={field.options || []}
                valueText="value"
                labelText="label"
                isClearable
                disabled={!bulkFields[field.name]}
                selectedOption={bulkForm[field.name]}
                setSelectedOption={(data) => onFieldChange(field.name, data)}
              />
            ) : field.type === "checkbox" ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <InputField
                  className="modal_input"
                  type="checkbox"
                  name={field.name}
                  disabled={!bulkFields[field.name]}
                  checked={bulkForm[field.name] === true}
                  onChange={({ target: { checked } }) => onFieldChange(field.name, checked)}
                />
                <span>{bulkForm[field.name] ? "Yes" : "No"}</span>
              </div>
            ) : (
              <InputField
                className="modal_input orderModal_Input"
                placeholder={`Enter ${field.label}`}
                name={field.name}
                type="text"
                disabled={!bulkFields[field.name]}
                value={bulkForm[field.name] || ""}
                onChange={({ target: { value } }) => onFieldChange(field.name, value)}
              />
            )}
          </div>
        ))}
      </div>
    </ModalBody>
    <ModalFooter>
      <Button color="secondary" onClick={onClose}>
        Cancel
      </Button>
      <Button color="primary" onClick={onSubmit}>
        Update Voters
      </Button>
    </ModalFooter>
  </Modal>
);

const BulkConfirmModal = ({ isOpen, count, loading, onCancel, onConfirm }) => (
  <Modal size="sm" isOpen={isOpen} toggle={onCancel} centered>
    <ModalHeader toggle={onCancel}>Confirm Bulk Update</ModalHeader>
    <ModalBody>You are about to update {count} voters. Continue?</ModalBody>
    <ModalFooter>
      <Button color="secondary" onClick={onCancel}>
        Cancel
      </Button>
      <Button color="primary" disabled={loading} onClick={onConfirm}>
        {loading ? "Updating..." : "Update Voters"}
      </Button>
    </ModalFooter>
  </Modal>
);
